import { Controller } from "@hotwired/stimulus"
import { categoryLabel, NO_BRAND_TILE, noBrandSvgHtml } from "../helpers/expense_constants"
import { intlLocale } from "../helpers/locale"

/**
 * Controller for the expense edit page.
 * Handles: category picker overlay, field sync, frequency enable/disable,
 * and dynamic renewal date calculation.
 */
export default class extends Controller {
  static targets = [
    "categoryPickerOverlay", "categoryGridItem",
    "categoryTriggerIcon", "categoryTriggerLabel",
    "categoryField", "iconField",
    "iconPickerOverlay",
    "iconTriggerIcon", "iconTriggerLabel",
    "iconClearButton", "brandIconField",
    "brandResults", "brandEmptyState",
    "pickerSelectedRow", "pickerSelectedTile", "pickerSelectedLabel",
    "pickerCategorySelectedTile", "pickerCategorySelectedLabel",
    "frequencySection", "recurringOnlySection",
    "renewalCard", "renewalText",
    "startDateField", "startDateDisplay",
    "reminderSection", "reminderField",
    "reminderTimeField", "reminderTimePreview",
    "typeSection", "amountField", "amountUnderline", "nameField",
    "amountHero"
  ]

  static values = {
    renewsTemplate: { type: String, default: "Renews: %{date}" },
    brandSearchUrl: { type: String, default: "" }
  }

  connect() {
    this._lastDateValue = this.hasStartDateFieldTarget ? this.startDateFieldTarget.value : null
    this._isIncome = this._selectedTypeIsIncome()
    this._syncStartDateDisplay()
    this._updateRenewalPreview()
    // Income never shows bill reminders; non-income keeps the
    // server-rendered visibility untouched on load.
    if (this._isIncome) this._updateReminderVisibility()

    // Snapshot server-rendered popular brands so the search-reset restores them
    // instantly with no re-fetch.
    if (this.hasBrandResultsTarget) {
      this._initialBrandsHTML = this.brandResultsTarget.innerHTML
    }

    // iOS WKWebView doesn't reliably fire change/input events on date inputs.
    if (this.hasStartDateFieldTarget) {
      this._datePoller = setInterval(() => {
        const val = this.startDateFieldTarget.value
        if (val !== this._lastDateValue) {
          this._lastDateValue = val
          this._syncStartDateDisplay()
          this._updateRenewalPreview()
        }
      }, 300)
    }
  }

  disconnect() {
    if (this._datePoller) {
      clearInterval(this._datePoller)
      this._datePoller = null
    }
  }

  // ── Category Picker ────────────────────────────────────────

  openCategoryPicker() {
    this.categoryPickerOverlayTarget.classList.remove("hidden")
    this._resetCategoryFilter()
  }

  _resetCategoryFilter() {
    const input = this.categoryPickerOverlayTarget.querySelector("[data-category-search]")
    if (input) input.value = ""
    this.categoryPickerOverlayTarget.querySelectorAll("[data-cat], .grid, .grid + *, .grid").forEach(el => el.classList.remove("hidden"))
    // Restore section headers (siblings before grids)
    this.categoryPickerOverlayTarget.querySelectorAll(".grid").forEach(grid => {
      const header = grid.previousElementSibling
      if (header) header.classList.remove("hidden")
    })
  }

  closeCategoryPicker() {
    this.categoryPickerOverlayTarget.classList.add("hidden")
  }

  filterCategories(e) {
    const q = (e.target.value || "").trim().toLowerCase()
    const overlay = this.categoryPickerOverlayTarget
    overlay.querySelectorAll("[data-cat]").forEach(btn => {
      const label = (btn.dataset.label || btn.textContent || "").toLowerCase()
      btn.classList.toggle("hidden", q.length > 0 && !label.includes(q))
    })
    overlay.querySelectorAll(".grid").forEach(grid => {
      const anyVisible = Array.from(grid.children).some(c => !c.classList.contains("hidden"))
      grid.classList.toggle("hidden", !anyVisible)
      const header = grid.previousElementSibling
      if (header) header.classList.toggle("hidden", !anyVisible)
    })
  }

  selectCategory(e) {
    const btn   = e.currentTarget
    const cat   = btn.dataset.cat
    const label = btn.dataset.label

    // Sync hidden field — icon stays independent (user picks via its own picker)
    this.categoryFieldTarget.value = cat
    this.categoryFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))

    // Update trigger icon — clone the tile (NOT the halo wrapper). The grid
    // button renders `.expense-icon-halo > .expense-icon-tile`; the halo's
    // 24px sizing doesn't cascade into the tile's inline width/height, so
    // cloning the halo makes the inner tile (rendered at icon_size, e.g. 56px)
    // overflow the trigger. Targeting `.expense-icon-tile` sidesteps that.
    const gridIcon = btn.querySelector(".expense-icon-tile")
    if (gridIcon) {
      const clone = gridIcon.cloneNode(true)
      // Match the initial server-rendered trigger tile (size: 38, radius: 12)
      // and the surrounding row icons (Name/When/Recurring all 38×38).
      clone.style.width  = "38px"
      clone.style.height = "38px"
      clone.style.borderRadius = "12px"
      const shadow = clone.dataset.shadow
      if (shadow) clone.style.boxShadow = `0 4px 10px ${shadow}40`
      // Match the visible SVG size used by the other row icons so the shape
      // fills the tile rather than rendering tiny inside lots of viewBox padding.
      const svg = clone.querySelector("svg")
      if (svg) {
        svg.setAttribute("width", "28")
        svg.setAttribute("height", "28")
      }
      this.categoryTriggerIconTarget.innerHTML = ""
      this.categoryTriggerIconTarget.appendChild(clone)
    }
    this.categoryTriggerLabelTarget.textContent = label

    // Mirror into in-picker hero card so user sees current pick instantly
    if (gridIcon && this.hasPickerCategorySelectedTileTarget) {
      const heroClone = gridIcon.cloneNode(true)
      heroClone.style.width = "38px"
      heroClone.style.height = "38px"
      heroClone.style.borderRadius = "16px"
      const heroSvg = heroClone.querySelector("svg")
      if (heroSvg) { heroSvg.setAttribute("width", "22"); heroSvg.setAttribute("height", "22") }
      this.pickerCategorySelectedTileTarget.innerHTML = ""
      this.pickerCategorySelectedTileTarget.appendChild(heroClone)
    }
    if (this.hasPickerCategorySelectedLabelTarget) {
      this.pickerCategorySelectedLabelTarget.textContent = label
    }

    // Highlight selected grid item
    this.categoryGridItemTargets.forEach(item => {
      const active = item.dataset.cat === cat
      item.classList.toggle("ring-2", active)
      item.classList.toggle("ring-indigo-400", active)
      item.classList.toggle("bg-indigo-50/30", active)
      item.classList.toggle("dark:bg-indigo-900/20", active)
    })

    this.closeCategoryPicker()
  }

  // ── Icon Picker ────────────────────────────────────────────

  openIconPicker() {
    if (!this.hasIconPickerOverlayTarget) return
    this.iconPickerOverlayTarget.classList.remove("hidden")
    this._resetIconFilter()
    // No fetch on open — popular brands are pre-rendered server-side, so the
    // picker is instant with no loading bar.
  }

  closeIconPicker() {
    if (!this.hasIconPickerOverlayTarget) return
    this.iconPickerOverlayTarget.classList.add("hidden")
  }

  _resetIconFilter() {
    const overlay = this.iconPickerOverlayTarget
    const input = overlay.querySelector("[data-icon-search]")
    if (input) input.value = ""
    if (this.hasBrandEmptyStateTarget) this.brandEmptyStateTarget.classList.add("hidden")
    if (this._initialBrandsHTML && this.hasBrandResultsTarget) {
      this.brandResultsTarget.innerHTML = this._initialBrandsHTML
    }
  }


  filterIcons(e) {
    const q = (e.target.value || "").trim()
    this._queueBrandSearch(q)
  }

  _queueBrandSearch(q) {
    if (this._brandTimer) clearTimeout(this._brandTimer)
    if (!q) {
      // Restore the server-rendered popular grid instantly — no network hit.
      if (this._initialBrandsHTML && this.hasBrandResultsTarget) {
        this.brandResultsTarget.innerHTML = this._initialBrandsHTML
      }
      if (this.hasBrandEmptyStateTarget) this.brandEmptyStateTarget.classList.add("hidden")
      return
    }
    if (!this.brandSearchUrlValue) return
    this._brandTimer = setTimeout(() => this._fetchBrands(q), 180)
  }

  async _fetchBrands(q) {
    try {
      const url = `${this.brandSearchUrlValue}?q=${encodeURIComponent(q)}`
      const r = await fetch(url, { headers: { Accept: "application/json" } })
      if (!r.ok) return
      const data = await r.json()
      this._renderBrands(data.brands || [])
    } catch (_) { /* ignore */ }
  }

  _renderBrands(brands) {
    if (!this.hasBrandResultsTarget) return
    const target = this.brandResultsTarget
    if (!brands.length) {
      target.innerHTML = ""
      if (this.hasBrandEmptyStateTarget) this.brandEmptyStateTarget.classList.remove("hidden")
      return
    }
    if (this.hasBrandEmptyStateTarget) this.brandEmptyStateTarget.classList.add("hidden")

    const grid = document.createElement("div")
    grid.className = "grid grid-cols-4 gap-1"
    brands.forEach(b => {
      const hex = b.hex || "94A3B8"
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.action = "click->expense-edit#selectBrandIcon"
      btn.dataset.brandSlug = b.slug
      btn.dataset.brandTitle = b.title
      btn.dataset.brandHex = hex
      btn.className = "flex flex-col items-center gap-1.5 py-2 px-1 transition-all duration-150 cursor-pointer"

      const tileWrap = document.createElement("span")
      tileWrap.className = "inline-flex"

      const tile = document.createElement("span")
      tile.className = "flex items-center justify-center shadow-sm"
      tile.setAttribute("data-brand-tile", "")
      tile.style.cssText = `width:56px;height:56px;border-radius:16px;background:#${hex};flex-shrink:0;`
      let svg = b.svg || ""
      svg = svg.replace(/<svg\b/, '<svg width="28" height="28" fill="#FFFFFF" style="fill:#FFFFFF"')
      tile.innerHTML = svg
      tileWrap.appendChild(tile)

      const label = document.createElement("span")
      label.className = "text-[11px] font-semibold leading-tight text-center text-slate-500 dark:text-slate-400 truncate max-w-full"
      label.textContent = b.title

      btn.appendChild(tileWrap)
      btn.appendChild(label)
      grid.appendChild(btn)
    })

    target.innerHTML = ""
    target.appendChild(grid)
  }

  selectBrandIcon(e) {
    const btn = e.currentTarget
    const slug = btn.dataset.brandSlug
    const title = btn.dataset.brandTitle || slug

    // Brand wins over icon; clear icon so display priority resolves to brand.
    if (this.hasBrandIconFieldTarget) {
      this.brandIconFieldTarget.value = slug
      this.brandIconFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this.iconFieldTarget.value = ""
    this.iconFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))

    // Clone brand tile to trigger. Server partial + JS-rendered brand tiles
    // both mark the colored tile with `data-brand-tile`.
    const brandTile = btn.querySelector("[data-brand-tile]")
    if (brandTile && this.hasIconTriggerIconTarget) {
      const clone = brandTile.cloneNode(true)
      clone.style.width = "38px"
      clone.style.height = "38px"
      clone.style.borderRadius = "12px"
      const svg = clone.querySelector("svg")
      if (svg) {
        svg.setAttribute("width", "26")
        svg.setAttribute("height", "26")
      }
      this.iconTriggerIconTarget.innerHTML = ""
      this.iconTriggerIconTarget.appendChild(clone)
    }
    if (this.hasIconTriggerLabelTarget) {
      this.iconTriggerLabelTarget.textContent = title
    }
    if (this.hasIconClearButtonTarget) {
      this.iconClearButtonTarget.classList.remove("hidden")
    }
    // Update brand-grid rings: clear old, ring new
    this._updateBrandGridRing(slug)

    // Update in-picker hero card with current pick
    this._updatePickerSelected(brandTile, title)

    this.closeIconPicker()
  }

  _updateBrandGridRing(activeSlug) {
    if (!this.hasBrandResultsTarget) return
    this.brandResultsTarget.querySelectorAll("[data-brand-slug]").forEach(btn => {
      const wrap = btn.querySelector("[data-brand-tile]")?.parentElement
      if (!wrap) return
      const active = btn.dataset.brandSlug === activeSlug
      wrap.classList.toggle("ring-2", active)
      wrap.classList.toggle("ring-indigo-400", active)
      wrap.classList.toggle("ring-offset-2", active)
      wrap.classList.toggle("rounded-[20px]", active)
      wrap.classList.toggle("p-[2px]", active)
    })
  }

  _updatePickerSelected(brandTileNode, title) {
    if (this.hasPickerSelectedRowTarget) this.pickerSelectedRowTarget.classList.remove("hidden")
    if (this.hasPickerSelectedTileTarget && brandTileNode) {
      const clone = brandTileNode.cloneNode(true)
      // Chip footprint — matches the shared picker_header partial (38×16)
      clone.style.width = "38px"
      clone.style.height = "38px"
      clone.style.borderRadius = "16px"
      const svg = clone.querySelector("svg")
      if (svg) { svg.setAttribute("width", "22"); svg.setAttribute("height", "22") }
      this.pickerSelectedTileTarget.innerHTML = ""
      this.pickerSelectedTileTarget.appendChild(clone)
    }
    if (this.hasPickerSelectedLabelTarget) {
      this.pickerSelectedLabelTarget.textContent = title
    }
  }

  clearIcon(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault()
    this.iconFieldTarget.value = ""
    this.iconFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))

    if (this.hasBrandIconFieldTarget) {
      this.brandIconFieldTarget.value = ""
      this.brandIconFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    // No brand → the storefront no-brand tile + "Other" label, never the category icon
    this._renderOtherIconTrigger()

    if (this.hasIconClearButtonTarget) {
      this.iconClearButtonTarget.classList.add("hidden")
    }
    if (this.hasPickerSelectedRowTarget) this.pickerSelectedRowTarget.classList.add("hidden")
    this._updateBrandGridRing(null)
    this.closeIconPicker()
  }

  _renderOtherIconTrigger() {
    if (this.hasIconTriggerIconTarget) {
      this.iconTriggerIconTarget.innerHTML = `
        <div class="expense-icon-tile flex items-center justify-center"
             style="width:38px;height:38px;border-radius:12px;flex-shrink:0;
                    --c-light:${NO_BRAND_TILE.light};--c-dark:${NO_BRAND_TILE.dark};">
          ${noBrandSvgHtml(28)}
        </div>
      `
    }
    if (this.hasIconTriggerLabelTarget) {
      this.iconTriggerLabelTarget.textContent = categoryLabel("other")
    }
  }

  // ── Recurring → Frequency enable/disable ───────────────────

  toggleFrequency(e) {
    const enabled = e.target.checked

    // Frequency pills live inside `recurringOnlySection` now, so hiding that
    // block takes care of them — no separate opacity/disabled handling needed.
    if (this.hasRecurringOnlySectionTarget) {
      this.recurringOnlySectionTarget.classList.toggle("hidden", !enabled)
    }

    this._updateRenewalPreview()
    this._updateReminderVisibility()
  }

  // ── Frequency / Date change → recalculate renewal ──────────

  onFrequencyChange() {
    this._updateRenewalPreview()
    this._updateReminderVisibility()
  }

  onStartDateChange() {
    const currentValue = this.hasStartDateFieldTarget ? this.startDateFieldTarget.value : null
    if (currentValue === this._lastDateValue) return
    this._lastDateValue = currentValue
    this._syncStartDateDisplay()
    this._updateRenewalPreview()
  }

  // ── Start date display sync ──────────────────────────────────

  _syncStartDateDisplay() {
    if (!this.hasStartDateDisplayTarget || !this.hasStartDateFieldTarget) return
    const val = this.startDateFieldTarget.value
    this.startDateDisplayTarget.textContent = val ? this._formatDate(val) : "—"
  }

  // ── Renewal calculation ────────────────────────────────────

  _updateRenewalPreview() {
    if (!this.hasRenewalCardTarget) return

    const startDate = this.hasStartDateFieldTarget ? this.startDateFieldTarget.value : null
    const frequency = this._selectedFrequency()
    const recurring = this._isRecurring()

    if (!recurring || !startDate || !frequency) {
      this.renewalCardTarget.classList.add("hidden")
      return
    }

    const renewal = this._computeNextRenewal(startDate, frequency)
    if (renewal) {
      const tmpl = this.renewsTemplateValue
      const formatted = this._formatDate(renewal)
      this.renewalTextTarget.textContent = tmpl.replace("%{date}", formatted)
      this.renewalCardTarget.classList.remove("hidden")
      this._pulseRenewalCard()
    } else {
      this.renewalCardTarget.classList.add("hidden")
    }
  }

  _selectedFrequency() {
    const checked = this.frequencySectionTarget.querySelector('input[type="radio"]:checked')
    return checked ? checked.value : null
  }

  _isRecurring() {
    const checkbox = this.element.querySelector('input[type="checkbox"][name*="recurring"]')
    return checkbox ? checkbox.checked : false
  }

  _computeNextRenewal(startDateISO, frequency) {
    if (!startDateISO || !frequency) return null
    const start = new Date(startDateISO + "T00:00:00")
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const advance = (d) => {
      switch (frequency) {
        case "daily":   d.setDate(d.getDate() + 1); break
        case "weekly":  d.setDate(d.getDate() + 7); break
        case "monthly": d.setMonth(d.getMonth() + 1); break
        case "yearly":  d.setFullYear(d.getFullYear() + 1); break
        default:        d.setMonth(d.getMonth() + 1)
      }
    }

    // Start date = first occurrence. Renewal = next occurrence after start.
    let candidate = new Date(start)
    advance(candidate)
    while (candidate < today) { advance(candidate) }

    const y = candidate.getFullYear()
    const m = String(candidate.getMonth() + 1).padStart(2, '0')
    const d = String(candidate.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  _formatDate(isoString) {
    if (!isoString) return ""
    const date = new Date(isoString + "T00:00:00")
    return date.toLocaleDateString(intlLocale(), {
      year: "numeric", month: "long", day: "numeric"
    })
  }

  reminderTimeChanged() {
    if (!this.hasReminderTimeFieldTarget || !this.hasReminderTimePreviewTarget) return
    const value = this.reminderTimeFieldTarget.value
    if (!value) return
    const [h, m] = value.split(":").map(Number)
    if (Number.isNaN(h) || Number.isNaN(m)) return
    const date = new Date()
    date.setHours(h, m, 0, 0)
    const formatted = date.toLocaleTimeString(intlLocale(), {
      hour: "numeric", minute: "2-digit", hour12: true
    })
    this.reminderTimePreviewTarget.textContent = formatted
  }

  _updateReminderVisibility() {
    if (!this.hasReminderSectionTarget) return

    const recurring = this._isRecurring()
    const frequency = this._selectedFrequency()
    const eligible = !this._isIncome && recurring && (frequency === "monthly" || frequency === "yearly")

    this.reminderSectionTarget.classList.toggle("hidden", !eligible)
  }

  // ── Expense | Income segmented toggle ──────────────────────

  onTypeChange() {
    this._applyType(this._selectedTypeIsIncome())
  }

  _selectedTypeIsIncome() {
    if (!this.hasTypeSectionTarget) return false
    return this.typeSectionTarget.querySelector("input:checked")?.value === "income"
  }

  _applyType(isIncome) {
    const changed = this._isIncome !== isIncome
    this._isIncome = isIncome

    if (this.hasTypeSectionTarget) {
      this.typeSectionTarget.classList.toggle("type-glide--income", isIncome)
    }
    if (this.hasAmountHeroTarget) {
      this.amountHeroTarget.classList.toggle("amount-hero--income", isIncome)
    }
    if (this.hasAmountFieldTarget) {
      this.amountFieldTarget.classList.toggle("text-emerald-600", isIncome)
      this.amountFieldTarget.classList.toggle("dark:text-emerald-400", isIncome)
      this.amountFieldTarget.classList.toggle("text-base-content", !isIncome)
    }
    if (this.hasAmountUnderlineTarget) {
      const underline = this.amountUnderlineTarget
      const emerald = ["from-emerald-300", "via-emerald-400", "to-emerald-300"]
      const blue = ["from-blue-300", "via-blue-400", "to-blue-300"]
      emerald.forEach((cls) => underline.classList.toggle(cls, isIncome))
      blue.forEach((cls) => underline.classList.toggle(cls, !isIncome))
      // Soft glow flash in the new identity color on every switch.
      if (changed) {
        underline.classList.remove("type-pulse-income", "type-pulse-expense")
        void underline.offsetWidth
        underline.classList.add(isIncome ? "type-pulse-income" : "type-pulse-expense")
      }
    }
    if (this.hasNameFieldTarget) {
      const { placeholderExpense, placeholderIncome } = this.nameFieldTarget.dataset
      this.nameFieldTarget.placeholder = isIncome ? placeholderIncome : placeholderExpense
    }

    this._updateReminderVisibility()
  }

  _pulseRenewalCard() {
    const card = this.renewalCardTarget
    card.classList.remove("renewal-pulse")
    // Force reflow to restart animation
    void card.offsetWidth
    card.classList.add("renewal-pulse")
  }
}
